export const QUERY_KEYS = {
  UNREAD_COUNT: ["messaging", "unread-count"] as const,
  NOTIFICATION_UNREAD_COUNT: ["notifications", "unread-count"] as const,
  NOTIFICATION_PREFERENCES: ["notifications", "preferences"] as const,
  USER_ANALYTICS_OVERVIEW: (range: "7d" | "30d" | "all") =>
    ["analytics", "user", "overview", range] as const,
  COMPANY_ANALYTICS_OVERVIEW: (
    companyId?: string | null,
    range?: "7d" | "30d" | "all",
  ) =>
    [
      "analytics",
      "company",
      companyId ?? "none",
      "overview",
      range ?? "30d",
    ] as const,
  COMPANY_ANALYTICS_TALENT: (
    companyId?: string | null,
    range?: "7d" | "30d" | "all",
  ) =>
    [
      "analytics",
      "company",
      companyId ?? "none",
      "talent",
      range ?? "30d",
    ] as const,
  COMPANY_ANALYTICS_JOB: (
    companyId?: string | null,
    jobId?: string | null,
    range?: "7d" | "30d" | "all",
  ) =>
    [
      "analytics",
      "company",
      companyId ?? "none",
      "job",
      jobId ?? "none",
      range ?? "30d",
    ] as const,
  PUBLIC_PROFILE_BUILDER: (
    mode: "individual" | "company",
    scopeId?: string | null,
  ) => ["public-profile", "builder", mode, scopeId ?? "personal"] as const,
  PUBLIC_PROFILE_ANALYTICS: (
    range: "7d" | "30d" | "90d" | "all",
    cvId?: string | null,
  ) => ["public-profile", "analytics", range, cvId ?? "all-cvs"] as const,
  PUBLIC_PROFILE_ANALYTICS_ACTOR: (
    range: "7d" | "30d" | "90d" | "all",
    cvId: string | null,
    kind: string,
    id: string | null,
  ) =>
    [
      "public-profile",
      "analytics-actor",
      range,
      cvId ?? "all-cvs",
      kind,
      id ?? "anonymous",
    ] as const,
  CV_VIEWING_PRIVACY: ["public-profile", "viewing-privacy"] as const,
  WORKSPACES: (userId?: string | null) =>
    ["workspaces", userId ?? "anon"] as const,
  SUBSCRIPTION_STATE: (
    userId?: string | null,
    workspaceId?: string | null,
    mode?: "seeker" | "employer" | null,
  ) =>
    [
      "subscription",
      "state",
      userId ?? "anon",
      workspaceId ?? "personal",
      mode ?? "seeker",
    ] as const,
  APPLICATION_STUDIO_PROJECTS: ["application-studio", "projects"] as const,
  APPLICATION_STUDIO_PROJECT: (projectId?: string | null) =>
    ["application-studio", "project", projectId ?? "none"] as const,
  APPLICATION_STUDIO_ARTIFACTS: (
    projectId?: string | null,
    kind?: string | null,
    limit?: number | null,
  ) =>
    [
      "application-studio",
      "artifacts",
      projectId ?? "all",
      kind ?? "all",
      limit ?? "default",
    ] as const,
  APPLICATION_STUDIO_ARTIFACT: (artifactId?: string | null) =>
    ["application-studio", "artifact", artifactId ?? "none"] as const,
  COMPANY_SEATS: (companyId?: string | null) =>
    ["subscription", "company", companyId ?? "none", "seats"] as const,
  JOB_CATALOG_DISCOVERY: (params?: string | null) =>
    ["job-catalog", "discover", params ?? "default"] as const,
  JOB_CATALOG_EXPLORE: (params?: string | null) =>
    ["job-catalog", "explore", params ?? "default"] as const,
  JOB_CATALOG_DETAIL: (id?: string | null) =>
    ["job-catalog", "detail", id ?? "none"] as const,
  JOB_CATALOG_HISTORY: (limit?: number | null) =>
    ["job-catalog", "history", limit ?? "default"] as const,
  JOB_CATALOG_SAVED: (limit?: number | null) =>
    ["job-catalog", "saved", limit ?? "default"] as const,
  JOB_CATALOG_AUTO_APPLY: ["job-catalog", "auto-apply"] as const,
  JOB_CATALOG_AUTO_APPLY_ACTIVITY_ROOT: [
    "job-catalog",
    "auto-apply",
    "activity",
  ] as const,
  JOB_CATALOG_AUTO_APPLY_ACTIVITY: (
    limit?: number | null,
    cursor?: string | null,
  ) =>
    [
      "job-catalog",
      "auto-apply",
      "activity",
      limit ?? "default",
      cursor ?? "start",
    ] as const,
  JOB_CATALOG_AUTO_APPLY_QUEUE: ["job-catalog", "auto-apply", "queue"] as const,
  TAXONOMY_SKILLS: (params?: string | null) =>
    ["taxonomy", "skills", params ?? "default"] as const,
  TAXONOMY_ROLES: (params?: string | null) =>
    ["taxonomy", "roles", params ?? "default"] as const,
  TAXONOMY_NORMALIZE_SKILLS: (params?: string | null) =>
    ["taxonomy", "normalize-skills", params ?? "default"] as const,
  JOB_DISCOVERY: (params?: string | null) =>
    ["jobs", "discover", params ?? "default"] as const,
  JOB_APPLICATIONS: (jobId?: string | null, limit?: number | null) =>
    ["jobs", "applications", jobId ?? "all", limit ?? "default"] as const,
  JOB_DETAIL: (id?: string | null) => ["jobs", "detail", id ?? "none"] as const,
  COMPANY_JOBS: (companyId?: string | null, limit?: number | null) =>
    ["jobs", "company", companyId ?? "none", limit ?? "default"] as const,
  EMPLOYER_APPLICANTS: (jobId?: string | null, params?: string | null) =>
    [
      "jobs",
      "company",
      jobId ?? "none",
      "applicants",
      params ?? "default",
    ] as const,
  EMPLOYER_APPLICANT_DETAIL: (
    jobId?: string | null,
    applicationId?: string | null,
  ) =>
    [
      "jobs",
      "company",
      jobId ?? "none",
      "applicants",
      applicationId ?? "none",
    ] as const,
  /** Company-wide Applications inbox (jobs + talent network union). */
  COMPANY_APPLICATIONS_ROOT: ["company-applications"] as const,
  COMPANY_APPLICATIONS: (params?: string | null) =>
    ["company-applications", "list", params ?? "default"] as const,
  TALENT_SUBMISSION_DETAIL: (submissionId?: string | null) =>
    ["company-applications", "talent", submissionId ?? "none"] as const,
  EMPLOYER_CANDIDATES: (companyId?: string | null, params?: string | null) =>
    ["employer-candidates", companyId ?? "none", params ?? "default"] as const,
  EMPLOYER_CANDIDATE_DETAIL_SCOPE: (
    companyId?: string | null,
    candidateUserId?: string | null,
  ) =>
    [
      "employer-candidates",
      companyId ?? "none",
      "detail",
      candidateUserId ?? "none",
    ] as const,
  EMPLOYER_CANDIDATE_DETAIL: (
    companyId?: string | null,
    candidateUserId?: string | null,
    searchSessionId?: string | null,
  ) =>
    [
      "employer-candidates",
      companyId ?? "none",
      "detail",
      candidateUserId ?? "none",
      searchSessionId ?? "no-search-session",
    ] as const,
  LIBRARY_OVERVIEW: (companyId?: string | null) =>
    ["library", companyId ?? "none", "overview"] as const,
  LIBRARY_ROLES: (companyId?: string | null) =>
    ["library", companyId ?? "none", "roles"] as const,
  LIBRARY_CANDIDATES: (companyId?: string | null, params?: string | null) =>
    [
      "library",
      companyId ?? "none",
      "candidates",
      params ?? "default",
    ] as const,
  LIBRARY_CANDIDATE_DETAIL_SCOPE: (candidateId?: string | null) =>
    ["library", "candidate", candidateId ?? "none"] as const,
  LIBRARY_CANDIDATE_DETAIL: (
    candidateId?: string | null,
    searchSessionId?: string | null,
  ) =>
    [
      "library",
      "candidate",
      candidateId ?? "none",
      searchSessionId ?? "no-search-session",
    ] as const,
  LIBRARY_COLLECTIONS: (companyId?: string | null) =>
    ["library", companyId ?? "none", "collections"] as const,
  LIBRARY_EMAIL_ADDRESS: (companyId?: string | null) =>
    ["library", companyId ?? "none", "email-address"] as const,
  LIBRARY_SAVED_SEARCHES: (companyId?: string | null) =>
    ["library", companyId ?? "none", "saved-searches"] as const,
  LIBRARY_PROJECTS: (companyId?: string | null) =>
    ["library", companyId ?? "none", "projects"] as const,
  LIBRARY_PROJECT_DETAIL: (
    companyId?: string | null,
    projectId?: string | null,
  ) =>
    ["library", companyId ?? "none", "project", projectId ?? "none"] as const,
  SHORTLISTS: (companyId?: string | null, params?: string | null) =>
    ["shortlists", companyId ?? "none", params ?? "default"] as const,
  SHORTLISTS_BY_COMPANY: (companyId?: string | null) =>
    ["shortlists", companyId ?? "none"] as const,
  SHORTLIST_DETAIL: (shortlistId?: string | null) =>
    ["shortlists", "detail", shortlistId ?? "none"] as const,
  SHORTLIST_CANDIDATES: (shortlistId?: string | null) =>
    ["shortlists", shortlistId ?? "none", "candidates"] as const,
  SHORTLIST_FEEDBACK: (shortlistId?: string | null) =>
    ["shortlists", shortlistId ?? "none", "feedback"] as const,
  SHORTLIST_FEEDBACK_SUMMARY: (shortlistId?: string | null) =>
    ["shortlists", shortlistId ?? "none", "feedback-summary"] as const,
  PUBLIC_SHORTLIST: (slug?: string | null) =>
    ["public-shortlists", slug ?? "none"] as const,
  PUBLIC_SHORTLIST_REVIEW_CANDIDATES: (slug?: string | null) =>
    ["public-shortlists", slug ?? "none", "candidates"] as const,
  PUBLIC_SHORTLIST_SESSION: (slug?: string | null, sessionId?: string | null) =>
    [
      "public-shortlists",
      slug ?? "none",
      "session",
      sessionId ?? "none",
    ] as const,
  CONVERSATIONS: ["messaging", "conversations"] as const,
  MESSAGING_PREFERENCES: ["messaging", "preferences"] as const,
  MESSAGING_BLOCKED: ["messaging", "blocked"] as const,
  MESSAGING_REPORTS: ["messaging", "reports"] as const,
  MESSAGING_CONVERSATION: (id: string) =>
    ["messaging", "conversation", id] as const,
  MESSAGING_GATED_RECIPIENT: (recipientId: string) =>
    ["messaging", "gated-recipient", recipientId] as const,
  AGENT_MODELS: (mode: "seeker" | "employer") =>
    ["agent", "models", mode] as const,
  AGENT_CAPABILITIES: ["agent", "capabilities"] as const,
  ARTIFACT: (id: string) => ["artifacts", "detail", id] as const,
  /** Blocking findings of an artifact's head version (the Publish sheet). */
  ARTIFACT_HEAD_CHECKS: (id: string) => ["artifacts", "head-checks", id] as const,
  /** Every published-site query shares this prefix (one invalidation refreshes them all). */
  SITES: ["sites"] as const,
  SITES_LIST: (tab: string, q: string) => ["sites", "list", tab, q] as const,
  SITE: (id: string) => ["sites", "detail", id] as const,
  ARTIFACT_SITE: (artifactId: string) => ["sites", "artifact", artifactId] as const,
  SITE_STATS: (id: string, days: number) => ["sites", "stats", id, days] as const,
  SITE_DEPLOYS: (id: string) => ["sites", "deploys", id] as const,
  ARTIFACT_SIGNED_URL: (
    id: string,
    variant: string,
    theme: string,
    generation: number,
    /** The version shown, or the head's version and revision, so an edit mints a new link. */
    revision = "",
  ) => ["artifacts", "signed-url", id, variant, theme, generation, revision] as const,
  /** Every query of one artifact's versions shares this prefix. */
  ARTIFACT_VERSIONS: (id: string) => ["artifacts", "versions", id] as const,
  ARTIFACT_VERSION: (id: string, versionId: string) =>
    ["artifacts", "versions", id, versionId] as const,
  THREADS: (mode: "seeker" | "employer", companyId?: string | null) =>
    ["agent", "threads", mode, companyId ?? "personal"] as const,
  THREAD_MESSAGES: (
    id: string,
    mode: "seeker" | "employer",
    companyId?: string | null,
  ) =>
    [
      "agent",
      "threads",
      mode,
      companyId ?? "personal",
      id,
      "messages",
    ] as const,
};
